<script setup lang="ts">
import { useFilterStore } from '@stores/useFilterStore.ts';
import { trans } from '@tools/translation.ts';
import { storeToRefs } from 'pinia';
import Sliderup from '@components/Sliderup.vue';
import MobileFiltersPanel from '@pages/search/components/filters/MobileFiltersPanel.vue';
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import {
  IconMagnifyingGlass,
  IconSlidersFill,
  IconArrowLeftBlack,
  IconArrowCounterClockwiseFillBlack,
} from '@icons';
import { useAuthStore } from '@stores/useAuthStore.ts';
import { watch } from 'vue';

const filterStore = useFilterStore();
const { isFilterOpen, currentMobileFilterStep, stepMobileFilterStack } = storeToRefs(filterStore);
const { closeAllMobileFilters, closeMobileFilter, openMobileFilter } = filterStore;

const handleClose = (): void => {
  if (stepMobileFilterStack.value.length === 1) {
    closeAllMobileFilters();
    return;
  }

  closeMobileFilter();
};

const { isImpersonated } = storeToRefs(useAuthStore());

watch(isFilterOpen, () => {
  (document.getElementById('navbar-mobile') as HTMLDivElement).style.display = isFilterOpen.value ? 'none' : 'block';
});
</script>

<template>
  <Button
    class="mobile-filters-button"
    variant="secondary"
    @click="openMobileFilter('mobile_filters')"
  >
    <Icon :svg="IconMagnifyingGlass" width="24" height="24" />

    <div class="ml-2 flex flex-grow justify-start">
      <Typography variant="body" color="gray-dark">
        {{ trans('search.filters.mobile.button') }}
      </Typography>
    </div>

    <Icon
      :svg="IconSlidersFill"
      width="24"
      height="24"
      fill="black"
    />
  </Button>

  <Sliderup
    :class="{ 'mt-12': isImpersonated }"
    :is-opened="isFilterOpen"
    :show-close-button="false"
    @close="handleClose"
  >
    <template #header>
      <div
        :class="[
          'flex shrink-0 items-center justify-between',
          'h-14 w-full',
          'p-2 pl-4',
          'border-0 border-b border-solid border-gray'
        ]"
      >
        <Icon
          :svg="IconArrowLeftBlack"
          width="24"
          height="24"
          @click="handleClose"
        />

        <Typography variant="content-heading-2">
          {{ trans(`search.filters.${currentMobileFilterStep}.title`) }}
        </Typography>

        <Button
          variant="secondary"
          class="!h-10 !w-[42px] !p-0"
        >
          <Icon
            :svg="IconArrowCounterClockwiseFillBlack"
            width="24"
            height="24"
          />
        </Button>
      </div>
    </template>

    <template #body>
      <MobileFiltersPanel
        v-if="currentMobileFilterStep"
        class="h-full"
        :current-mobile-filter-step="currentMobileFilterStep"
      />
    </template>

    <template #footer>
      <div class="p-2">
        <Button
          variant="primary"
          @click="closeMobileFilter"
        >
          {{ trans('search.filters.mobile.button') }}
        </Button>
      </div>
    </template>
  </Sliderup>
</template>

<style lang="scss" scoped>
.mobile-filters-button {
  @apply shadow-[0_4px_8px_0_rgba(0,0,0,0.25)];

  @apply justify-start;

  @apply h-14 px-3 py-4;

  @apply bg-white;
}

:deep(.sliderup-title) {
  @apply p-0 !important;
}
</style>
